import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { appendTeamMemberLawyerId } from "../../../../styles/styles";
import { backend_api } from "../../../../static/server";

const resolveTeamMemberLawyerId = (teamMemberLawyerId, getState) =>
  teamMemberLawyerId ?? getState?.()?.user?.teamMemberLawyerId ?? null;

export const fetchBatchCustomFieldUsage = createAsyncThunk(
  "task/fetchBatchCustomFieldUsage",
  async ({ requests, token }, { rejectWithValue }) => {
    try {
      const batchResults = [];

      for (const request of requests) {
        const { fieldName, fieldValue, customIds } = request;

        if (!fieldValue || !customIds || customIds.length === 0) {
          batchResults.push({
            cases: [],
            contacts: [],
            fieldName,
            success: false,
          });
          continue;
        }
        try {
          const response = await axios.get(
            `${backend_api}fetch-custom-field-usage`,
            {
              headers: {
                Authorization: `Token ${token}`,
              },
              params: {
                field_name: fieldName,
                value: fieldValue,
                custom_ids: customIds.join(","),
              },
            }
          );

          if (response.data.status === "success") {
            batchResults.push({
              ...response.data.data,
              fieldName,
              success: true,
            });
          } else {
            batchResults.push({
              cases: [],
              contacts: [],
              fieldName,
              success: false,
            });
          }
        } catch (error) {
          console.error(
            `Error fetching custom field usage for ${fieldName}:`,
            error
          );
          batchResults.push({
            cases: [],
            contacts: [],
            fieldName,
            success: false,
          });
        }
      }
      return batchResults;
    } catch (error) {
      console.error("Error in batch custom field usage:", error);
      return rejectWithValue("Failed to fetch related cases");
    }
  }
);
export const createTask = createAsyncThunk(
  "createTask",
  async ({ url, body, token, teamMemberLawyerId }, { getState }) => {
    try {
      const finalUrl = appendTeamMemberLawyerId(
        url,
        resolveTeamMemberLawyerId(teamMemberLawyerId, getState),
      );
      const response = await axios.post(finalUrl, body, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      return response?.data;
    } catch (error) {
      throw error?.response?.data || error?.message;
    }
  }
);

export const createTaskTemplate = createAsyncThunk(
  "createTaskTemplate",
  async ({ url, body, token, teamMemberLawyerId }, { getState }) => {
    try {
      const finalUrl = appendTeamMemberLawyerId(
        url,
        resolveTeamMemberLawyerId(teamMemberLawyerId, getState),
      );
      const response = await axios.post(finalUrl, body, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      return response?.data;
    } catch (error) {
      throw error?.response?.data || error?.message;
    }
  }
);

export const createTaskList = createAsyncThunk(
  "createTaskList",
  async ({ url, body, token, teamMemberLawyerId }, { getState }) => {
    try {
      const finalUrl = appendTeamMemberLawyerId(
        url,
        resolveTeamMemberLawyerId(teamMemberLawyerId, getState),
      );
      const response = await axios.post(finalUrl, body, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      return response?.data;
    } catch (error) {
      throw error?.response?.data || error?.message;
    }
  }
);

export const createTaskType = createAsyncThunk(
  "createTaskType",
  async ({ url, body, token, teamMemberLawyerId }, { getState }) => {
    try {
      const finalUrl = appendTeamMemberLawyerId(
        url,
        resolveTeamMemberLawyerId(teamMemberLawyerId, getState),
      );
      const response = await axios.post(finalUrl, body, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      toast.error("Failed to create Task type");
      throw error.response?.data || error.message;
    }
  }
);

export const getAllTaskType = createAsyncThunk(
  "getAllTaskType",
  async ({ url, token, page, pageSize, teamMemberLawyerId }) => {
    try {
      let paginatedUrl = appendTeamMemberLawyerId(url, teamMemberLawyerId);
      const params = [];
      if (page !== undefined) {
        params.push(`page=${page}`);
      }
      if (pageSize !== undefined) {
        params.push(`page_size=${pageSize}`);
      }
      if (params.length > 0) {
        const separator = paginatedUrl.includes("?") ? "&" : "?";
        paginatedUrl = `${paginatedUrl}${separator}${params.join("&")}`;
      }

      const response = await axios.get(paginatedUrl, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      return {
        ...response.data,
        pagination: {
          page: page || 1,
          pageSize: pageSize || 10,
          rowCount: response.data.count || 0,
          nextPage: response.data.next,
          previousPage: response.data.previous,
        },
      };
    } catch (error) {
      toast.error("Failed to get task type");
      throw error.response?.data || error.message;
    }
  }
);

// In your TaskSlice.js
export const getAllTask = createAsyncThunk(
  "getAllTask",
  async ({
    url,
    token,
    page,
    pageSize,
    search,
    searchKey = "task_name__icontains",
    teamMemberLawyerId,
    filters = {},
  }) => {
    try {
      let paginatedUrl = appendTeamMemberLawyerId(url, teamMemberLawyerId);
      const params = [];
      const hasCaseFilter = Array.isArray(filters.case)
        ? filters.case.length > 0
        : Boolean(filters.case);
      const hasCaseInFilter = Array.isArray(filters.case__in)
        ? filters.case__in.length > 0
        : Boolean(filters.case__in);

      if (page !== undefined) {
        params.push(`page=${page}`);
      }

      if (pageSize !== undefined) {
        params.push(`page_size=${pageSize}`);
      }

      if (search) {
        params.push(`${searchKey}=${encodeURIComponent(search)}`);
      }

      if (filters.priority && filters.priority.length > 0) {
        params.push(
          `priority__in=${encodeURIComponent(filters.priority.join(","))}`
        );
      }

      if (typeof filters.private_task === "boolean") {
        params.push(`private_task=${filters.private_task}`);
      }

      // Status filter
      if (filters.status && filters.status.length > 0) {
        const statusMap = {
          Pending: "Open",
          "In Progress": "Transfered",
          "In Review": "Reviewed",
          Complete: "Completed",
          "Not Need": "",
        };

        const mappedStatuses = filters.status
          .map((s) => statusMap[s] || s) // map to backend
          .filter((s) => s); // remove empty values

        if (mappedStatuses.length > 0) {
          const statusParam = mappedStatuses.map(encodeURIComponent).join(",");
          params.push(`status__in=${statusParam}`);
        }
      }

      // Date range filters
      if (filters.created_date__gte) {
        params.push(
          `created_date__gte=${encodeURIComponent(filters.created_date__gte)}`
        );
      }
      if (filters.created_date__lte) {
        params.push(
          `created_date__lte=${encodeURIComponent(filters.created_date__lte)}`
        );
      }
      if (filters.closed_on__gte) {
        params.push(
          `closed_on__gte=${encodeURIComponent(filters.closed_on__gte)}`
        );
      }
      if (filters.closed_on__lte) {
        params.push(
          `closed_on__lte=${encodeURIComponent(filters.closed_on__lte)}`
        );
      }

      // Specific date filters
      if (filters.due_date) {
        params.push(`closed_on=${encodeURIComponent(filters.due_date)}`);
      }
      if (filters.estimate_time) {
        params.push(
          `estimate_time=${encodeURIComponent(filters.estimate_time)}`
        );
      }
      if (filters.closed_on) {
        params.push(`closed_on=${encodeURIComponent(filters.closed_on)}`);
      }
      if (filters.created_date) {
        params.push(`created_date=${encodeURIComponent(filters.created_date)}`);
      }
      if (filters.updated_date) {
        params.push(`updated_date=${encodeURIComponent(filters.updated_date)}`);
      }
      if (filters.start_date) {
        params.push(`start_date=${encodeURIComponent(filters.start_date)}`);
      }

      // Assigned to filter
      if (filters.assigned_to) {
        params.push(`assign_to=${encodeURIComponent(filters.assigned_to)}`);
      }

      // Case filter
      if (hasCaseInFilter) {
        const caseIds = Array.isArray(filters.case__in)
          ? filters.case__in.join(",")
          : filters.case__in;
        params.push(`case__in=${encodeURIComponent(caseIds)}`);
      } else if (hasCaseFilter) {
        if (Array.isArray(filters.case)) {
          const caseIds = filters.case.filter((caseId) => caseId).join(",");
          if (caseIds) {
            params.push(`case__in=${encodeURIComponent(caseIds)}`);
          }
        } else {
          params.push(`case=${encodeURIComponent(filters.case)}`);
        }
      }

      // Client filter
      if (filters.client__in && !hasCaseFilter && !hasCaseInFilter) {
        const clientIds = Array.isArray(filters.client__in)
          ? filters.client__in.join(",")
          : filters.client__in;
        params.push(`client__in=${encodeURIComponent(clientIds)}`);
      } else if (filters.client && !hasCaseFilter && !hasCaseInFilter) {
        if (Array.isArray(filters.client)) {
          const clientIds = filters.client.filter((clientId) => clientId).join(",");
          if (clientIds) {
            params.push(`client__in=${encodeURIComponent(clientIds)}`);
          }
        } else {
          params.push(`client=${encodeURIComponent(filters.client)}`);
        }
      }

      // People/Company filters
      if (filters.is_people === true && !hasCaseFilter && !hasCaseInFilter) {
        params.push(`is_people=true`);
      }
      if (filters.is_company === true && !hasCaseFilter && !hasCaseInFilter) {
        params.push(`is_company=true`);
      }

      // Custom field filters - send as JSON string
      // if (filters.customFields && filters.customFields.length > 0) {
      //   const customFieldFilters = filters.customFields
      //     .filter(
      //       (cf) =>
      //         cf?.value !== undefined && cf?.value !== null && cf.value !== ""
      //     )
      //     .map((cf) => ({
      //       value: cf.value,
      //       name: cf.fieldData?.name || cf.name,
      //       ids: cf.customFieldIds || [],
      //     }));

      //   if (customFieldFilters.length > 0) {
      //     params.push(
      //       `custom_field_filters=${encodeURIComponent(
      //         JSON.stringify(customFieldFilters)
      //       )}`
      //     );
      //   }
      // }

      if (params.length > 0) {
        const separator = paginatedUrl.includes("?") ? "&" : "?";
        paginatedUrl = `${paginatedUrl}${separator}${params.join("&")}`;
      }

      const response = await axios.get(paginatedUrl, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      return {
        ...response.data,
        pagination: {
          page: page || 1,
          pageSize: pageSize || 10,
          rowCount: response.data.count || 0,
          nextPage: response.data.next,
          previousPage: response.data.previous,
        },
      };
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toast.error("Failed to get tasks");
      throw error.response?.data || error.message;
    }
  }
);

export const getAllTaskTemplate = createAsyncThunk(
  "getAllTaskTemplate",
  async ({
    url,
    token,
    page,
    pageSize,
    search,
    searchKey = "task_name__icontains",
    teamMemberLawyerId,
    filters = {},
  }) => {
    try {
      let paginatedUrl = appendTeamMemberLawyerId(url, teamMemberLawyerId);
      const params = [];
      if (page !== undefined) {
        params.push(`page=${page}`);
      }
      if (pageSize !== undefined) {
        params.push(`page_size=${pageSize}`);
      }
      if (search) {
        params.push(`${searchKey}=${encodeURIComponent(search)}`);
      }
      if (filters.priority && filters.priority.length > 0) {
        params.push(
          `priority__in=${encodeURIComponent(filters.priority.join(","))}`
        );
      }

      if (filters.status && filters.status.length > 0) {
        const statusMap = {
          Pending: "Open",
          "In Progress": "Transfered",
          "In Review": "Reviewed",
          Complete: "Completed",
          "Not Need": "",
        };

        const mappedStatuses = filters.status
          .map((s) => statusMap[s] || s)
          .filter((s) => s);

        if (mappedStatuses.length > 0) {
          const statusParam = mappedStatuses.map(encodeURIComponent).join(",");
          params.push(`status__in=${statusParam}`);
        }
      }
      if (filters.is_practice_area && filters.practice_area) {
        params.push(
          `is_practice_area=${encodeURIComponent(
            filters.is_practice_area
          )}&practice_area=${encodeURIComponent(filters.practice_area)}`
        );
      }
      if (filters.is_people) {
        params.push(`is_people=${encodeURIComponent(filters.is_people)}`);
      }
      if (filters.is_company) {
        params.push(`is_company=${encodeURIComponent(filters.is_company)}`);
      }
      if (filters.created_date) {
        params.push(`created_date=${encodeURIComponent(filters.created_date)}`);
      }
      if (filters.updated_date) {
        params.push(`updated_date=${encodeURIComponent(filters.updated_date)}`);
      }
      if (params.length > 0) {
        const separator = paginatedUrl.includes("?") ? "&" : "?";
        paginatedUrl = `${paginatedUrl}${separator}${params.join("&")}`;
      }

      const response = await axios.get(paginatedUrl, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      return {
        ...response.data,
        pagination: {
          page: page || 1,
          pageSize: pageSize || 10,
          rowCount: response.data.count || 0,
          nextPage: response.data.next,
          previousPage: response.data.previous,
        },
      };
    } catch (error) {
      toast.error("Failed to get task templates");
      throw error.response?.data || error.message;
    }
  }
);

export const getAllTaskList = createAsyncThunk(
  "getAllTaskList",
  async ({
    url,
    token,
    page,
    pageSize,
    search,
    searchKey = "task_name__icontains",
    teamMemberLawyerId,
    filters = {},
  }) => {
    try {
      let paginatedUrl = appendTeamMemberLawyerId(url, teamMemberLawyerId);
      const params = [];

      if (page !== undefined) {
        params.push(`page=${page}`);
      }

      if (pageSize !== undefined) {
        params.push(`page_size=${pageSize}`);
      }

      if (search) {
        params.push(`${searchKey}=${encodeURIComponent(search)}`);
      }

      if (filters.is_practice_area && filters.practice_area) {
        params.push(
          `is_practice_area=${encodeURIComponent(
            filters.is_practice_area
          )}&practice_area=${encodeURIComponent(filters.practice_area)}`
        );
      }
      if (filters.is_people) {
        params.push(`is_people=${encodeURIComponent(filters.is_people)}`);
      }
      if (filters.is_company) {
        params.push(`is_company=${encodeURIComponent(filters.is_company)}`);
      }
      if (filters.created_date) {
        params.push(`created_date=${encodeURIComponent(filters.created_date)}`);
      }

      if (params.length > 0) {
        const separator = paginatedUrl.includes("?") ? "&" : "?";
        paginatedUrl = `${paginatedUrl}${separator}${params.join("&")}`;
      }

      const response = await axios.get(paginatedUrl, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      return {
        ...response.data,
        pagination: {
          page: page || 1,
          pageSize: pageSize || 10,
          rowCount: response.data.count || 0,
          nextPage: response.data.next,
          previousPage: response.data.previous,
        },
      };
    } catch (error) {
      toast.error("Failed to get task list");
      throw error.response?.data || error.message;
    }
  }
);

export const updateTaskOrType = createAsyncThunk(
  "updateTaskOrType",
  async ({ url, body, token, teamMemberLawyerId }, { getState }) => {
    try {
      const finalUrl = appendTeamMemberLawyerId(
        url,
        resolveTeamMemberLawyerId(teamMemberLawyerId, getState),
      );
      const response = await axios.patch(finalUrl, body, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }
);

export const updateTaskList = createAsyncThunk(
  "task/updateTaskList",
  async ({ url, body, token, teamMemberLawyerId }, { rejectWithValue, getState }) => {
    try {
      const finalUrl = appendTeamMemberLawyerId(
        url,
        resolveTeamMemberLawyerId(teamMemberLawyerId, getState),
      );
      const response = await axios.put(finalUrl, body, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

const TaskSlice = createSlice({
  name: "TaskSlice",
  initialState: {
    allTask: null,
    taskPagination: {
      page: 1,
      pageSize: 10,
      rowCount: 0,
      nextPage: null,
      previousPage: null,
    },
    allTaskType: null,
    taskTypePagination: {
      page: 1,
      pageSize: 10,
      rowCount: 0,
      nextPage: null,
      previousPage: null,
    },
    allTaskList: null,
    taskListPagination: {
      pageSize: 10,
      rowCount: 0,
      nextPage: null,
      previousPage: null,
    },
    allTaskTemplate: null,
    taskTemplatePagination: {
      page: 1,
      pageSize: 10,
      rowCount: 0,
      nextPage: null,
      previousPage: null,
    },
    customFieldUsage: {
      loading: false,
      data: null,
      error: null,
    },
    loading: false,
    error: null,
  },
  reducers: {
    clearCustomFieldUsage: (state) => {
      state.customFieldUsage = {
        loading: false,
        data: null,
        error: null,
      };
    },
    upsertTaskFromLexmomAi: (state, action) => {
      const nextTask = action.payload;

      if (!nextTask || typeof nextTask !== "object") {
        return;
      }

      if (!Array.isArray(state.allTask)) {
        if (state.allTask == null) {
          state.allTask = [nextTask];
        }
        return;
      }

      const existingIndex = state.allTask.findIndex(
        (task) => task?.id === nextTask.id,
      );

      if (existingIndex >= 0) {
        state.allTask[existingIndex] = {
          ...state.allTask[existingIndex],
          ...nextTask,
        };
        return;
      }

      state.allTask.unshift(nextTask);

      if (typeof state.taskPagination?.rowCount === "number") {
        state.taskPagination.rowCount += 1;
      }
      if (typeof state.taskPagination?.count === "number") {
        state.taskPagination.count += 1;
      }
    },
    resetTaskState: (state) => {
      state.allTask = null;
      state.taskPagination = {
        page: 1,
        pageSize: 10,
        rowCount: 0,
        nextPage: null,
        previousPage: null,
      };
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Batch custom field usage
      .addCase(fetchBatchCustomFieldUsage.pending, (state) => {
        state.customFieldUsage.loading = true;
        state.customFieldUsage.error = null;
      })
      .addCase(fetchBatchCustomFieldUsage.fulfilled, (state, action) => {
        state.customFieldUsage.loading = false;
        state.customFieldUsage.data = action.payload;
      })
      .addCase(fetchBatchCustomFieldUsage.rejected, (state, action) => {
        state.customFieldUsage.loading = false;
        state.customFieldUsage.error = action.payload;
      })
      // Existing cases...
      .addCase(createTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTask.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create task type";
      })
      .addCase(createTaskType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTaskType.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createTaskType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create task type";
      })
      .addCase(getAllTaskType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllTaskType.fulfilled, (state, action) => {
        state.loading = false;
        state.allTaskType = action?.payload?.results;
        state.taskTypePagination = {
          ...action?.payload?.pagination,
          count: action?.payload?.count,
          next: action?.payload?.next,
          previous: action?.payload?.previous,
        };
      })
      .addCase(getAllTaskType.rejected, (state, action) => {
        state.loading = false;
        state.allTaskType = action?.payload || "Failed to get task type";
      })
      .addCase(getAllTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllTask.fulfilled, (state, action) => {
        state.loading = false;
        state.allTask = action?.payload?.results;
        state.taskPagination = {
          ...action?.payload?.pagination,
          count: action?.payload?.count,
          next: action?.payload?.next,
          previous: action?.payload?.previous,
        };
      })
      .addCase(getAllTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to get tasks";
      })
      .addCase(updateTaskOrType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTaskOrType.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateTaskOrType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to edit task type";
      })
      .addCase(updateTaskList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTaskList.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateTaskList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to edit task list";
      })
      .addCase(createTaskList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTaskList.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createTaskList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create task list";
      })
      .addCase(getAllTaskList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllTaskList.fulfilled, (state, action) => {
        state.loading = false;
        state.allTaskList = action?.payload?.results;
        state.taskListPagination = {
          ...action?.payload?.pagination,
          count: action?.payload?.count,
          next: action?.payload?.next,
          previous: action?.payload?.previous,
        };
      })
      .addCase(getAllTaskList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to get task list";
      })
      .addCase(createTaskTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTaskTemplate.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createTaskTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create task template";
      })
      .addCase(getAllTaskTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllTaskTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.allTaskTemplate = action?.payload?.results;
        state.taskTemplatePagination = {
          ...action?.payload?.pagination,
          count: action?.payload?.count,
          next: action?.payload?.next,
          previous: action?.payload?.previous,
        };
      })
      .addCase(getAllTaskTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to get task templates";
      });
  },
});

export const { clearCustomFieldUsage, resetTaskState, upsertTaskFromLexmomAi } =
  TaskSlice.actions;
export const TaskSliceReducer = TaskSlice.reducer;
